import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutGrid,
  List,
  Search,
  Star,
  Instagram,
  Youtube,
  Loader2,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  X,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchCreators,
  fetchCreatorFilterMeta,
  createCreator,
  exportCreators,
  bulkImportCreators,
} from "@/lib/api";
import { formatFollowers, formatINR } from "@/lib/format";
import FormDialog from "@/components/FormDialog";

const FOLLOWER_TIERS = [
  { label: "Any", value: 0 },
  { label: "10K+", value: 10000 },
  { label: "100K+", value: 100000 },
  { label: "500K+", value: 500000 },
  { label: "1M+", value: 1000000 },
];

const MAX_FOLLOWER_OPTIONS = [
  { label: "No cap", value: 0 },
  { label: "Up to 50K", value: 50000 },
  { label: "Up to 250K", value: 250000 },
  { label: "Up to 1M", value: 1000000 },
  { label: "Up to 5M", value: 5000000 },
];

const PRICE_RANGES = [
  { label: "Any price", min: 0, max: 0 },
  { label: "Under ₹25K", min: 0, max: 25000 },
  { label: "₹25K – ₹75K", min: 25000, max: 75000 },
  { label: "₹75K – ₹2L", min: 75000, max: 200000 },
  { label: "₹2L – ₹5L", min: 200000, max: 500000 },
  { label: "₹5L+", min: 500000, max: 0 },
];

const SORT_OPTIONS = [
  { label: "Followers (high → low)", sort_by: "followers", sort_dir: "desc" },
  { label: "Followers (low → high)", sort_by: "followers", sort_dir: "asc" },
  { label: "Price (high → low)", sort_by: "price", sort_dir: "desc" },
  { label: "Price (low → high)", sort_by: "price", sort_dir: "asc" },
  { label: "Rating (high → low)", sort_by: "rating", sort_dir: "desc" },
  { label: "Engagement rate (high → low)", sort_by: "er", sort_dir: "desc" },
  { label: "Name (A → Z)", sort_by: "name", sort_dir: "asc" },
];

const PAGE_SIZE = 12;

const PlatformIcon = ({ name }) =>
  name === "YouTube" ? <Youtube className="h-3.5 w-3.5" /> : <Instagram className="h-3.5 w-3.5" />;

const initialFilters = {
  q: "",
  niche: "All",
  platform: "All",
  city: "All",
  language: "All",
  minFollowers: 0,
  maxFollowers: 0,
  priceIdx: 0,
  minRating: 0,
};

const Creators = () => {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [filters, setFilters] = useState(initialFilters);
  const [debouncedQ, setDebouncedQ] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sortIdx, setSortIdx] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [meta, setMeta] = useState({ cities: [], languages: [], niches: [], platforms: [] });
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  // Load filter meta once
  useEffect(() => {
    fetchCreatorFilterMeta()
      .then(setMeta)
      .catch(() => {});
  }, []);

  // Debounce search query 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q.trim()), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  // Reset to page 1 when filters/sort change
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [
    debouncedQ,
    filters.niche,
    filters.platform,
    filters.city,
    filters.language,
    filters.minFollowers,
    filters.maxFollowers,
    filters.priceIdx,
    filters.minRating,
    sortIdx,
  ]);

  const queryParams = useMemo(() => {
    const sort = SORT_OPTIONS[sortIdx];
    const price = PRICE_RANGES[filters.priceIdx];
    const params = {
      sort_by: sort.sort_by,
      sort_dir: sort.sort_dir,
    };
    if (debouncedQ) params.q = debouncedQ;
    if (filters.niche !== "All") params.niche = filters.niche;
    if (filters.platform !== "All") params.platform = filters.platform;
    if (filters.city !== "All") params.city = filters.city;
    if (filters.language !== "All") params.language = filters.language;
    if (filters.minFollowers > 0) params.min_followers = filters.minFollowers;
    if (filters.maxFollowers > 0) params.max_followers = filters.maxFollowers;
    if (price.min > 0) params.min_price = price.min;
    if (price.max > 0) params.max_price = price.max;
    if (filters.minRating > 0) params.min_rating = filters.minRating;
    return params;
  }, [debouncedQ, filters, sortIdx]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCreators({ ...queryParams, page, page_size: PAGE_SIZE });
      setData(res);
    } catch (_e) {
      setData({ items: [], total: 0, page: 1, total_pages: 0 });
    } finally {
      setLoading(false);
    }
  }, [queryParams, page]);

  useEffect(() => {
    load();
  }, [load]);

  const items = data.items || [];
  const showingFrom = items.length === 0 ? 0 : (data.page - 1) * PAGE_SIZE + 1;
  const showingTo = (data.page - 1) * PAGE_SIZE + items.length;

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(data.total_pages || 1, p + 1));

  const allSelectedOnPage =
    items.length > 0 && items.every((c) => selectedIds.has(c.id));

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        items.forEach((c) => next.delete(c.id));
      } else {
        items.forEach((c) => next.add(c.id));
      }
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filters.niche !== "All") chips.push({ key: "niche", label: filters.niche });
    if (filters.platform !== "All") chips.push({ key: "platform", label: filters.platform });
    if (filters.city !== "All") chips.push({ key: "city", label: filters.city });
    if (filters.language !== "All") chips.push({ key: "language", label: filters.language });
    if (filters.minFollowers > 0)
      chips.push({ key: "minFollowers", label: `Followers ≥ ${formatFollowers(filters.minFollowers)}` });
    if (filters.maxFollowers > 0)
      chips.push({ key: "maxFollowers", label: `Followers ≤ ${formatFollowers(filters.maxFollowers)}` });
    if (filters.priceIdx > 0) chips.push({ key: "priceIdx", label: PRICE_RANGES[filters.priceIdx].label });
    if (filters.minRating > 0) chips.push({ key: "minRating", label: `Rating ≥ ${filters.minRating}` });
    return chips;
  }, [filters]);

  const clearChip = (key) => {
    setFilters((f) => ({
      ...f,
      ...(key === "niche" ? { niche: "All" } : {}),
      ...(key === "platform" ? { platform: "All" } : {}),
      ...(key === "city" ? { city: "All" } : {}),
      ...(key === "language" ? { language: "All" } : {}),
      ...(key === "minFollowers" ? { minFollowers: 0 } : {}),
      ...(key === "maxFollowers" ? { maxFollowers: 0 } : {}),
      ...(key === "priceIdx" ? { priceIdx: 0 } : {}),
      ...(key === "minRating" ? { minRating: 0 } : {}),
    }));
  };

  const resetFilters = () => setFilters(initialFilters);

  const onExport = async (format) => {
    setBusy(true);
    try {
      const params = { ...queryParams };
      if (selectedIds.size > 0) {
        params.ids = Array.from(selectedIds);
      }
      await exportCreators(format, params);
      const scope = selectedIds.size > 0 ? `${selectedIds.size} selected` : `${data.total} creators`;
      toast.success(`${format.toUpperCase()} export ready · ${scope}`);
    } catch (e) {
      toast.error(`Export failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onImportClick = () => fileInputRef.current?.click();

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be picked again
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please pick a .csv file");
      return;
    }
    setBusy(true);
    try {
      const res = await bulkImportCreators(file);
      const skippedMsg = res.skipped > 0 ? ` · ${res.skipped} skipped` : "";
      toast.success(`Imported ${res.inserted} creators${skippedMsg}`, {
        description:
          res.errors?.length > 0
            ? res.errors.slice(0, 3).map((er) => `Row ${er.row}: ${er.reason}`).join(" · ")
            : undefined,
      });
      await load();
    } catch (err) {
      toast.error(`Import failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="creators-page">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={onImportFile}
        data-testid="creators-import-input"
      />

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Creators</h1>
          <p className="text-sm text-zinc-500 mt-1" data-testid="creators-summary">
            {data.total} total · showing {showingFrom}–{showingTo}
            {selectedIds.size > 0 ? ` · ${selectedIds.size} selected` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAdvanced((v) => !v)}
            className="h-9 border-white/10 bg-white/[0.02] text-zinc-200 hover:bg-white/5"
            data-testid="toggle-advanced-filters"
          >
            <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
            {showAdvanced ? "Hide filters" : "Advanced filters"}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-9 border-white/10 bg-white/[0.02] text-zinc-200 hover:bg-white/5"
                data-testid="creators-sort-trigger"
              >
                <ArrowUpDown className="h-3.5 w-3.5 mr-1.5" />
                {SORT_OPTIONS[sortIdx].label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10">
              <DropdownMenuLabel className="text-zinc-400 text-xs">Sort creators</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/5" />
              {SORT_OPTIONS.map((s, i) => (
                <DropdownMenuItem
                  key={s.label}
                  onClick={() => setSortIdx(i)}
                  className={`text-zinc-200 focus:bg-white/5 ${i === sortIdx ? "text-blue-300" : ""}`}
                  data-testid={`creators-sort-option-${i}`}
                >
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={busy || items.length === 0}
                className="h-9 border-white/10 bg-white/[0.02] text-zinc-200 hover:bg-white/5"
                data-testid="creators-export-trigger"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10">
              <DropdownMenuLabel className="text-zinc-400 text-xs">
                {selectedIds.size > 0
                  ? `Export ${selectedIds.size} selected`
                  : `Export all ${data.total} (filtered)`}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem
                onClick={() => onExport("csv")}
                className="text-zinc-200 focus:bg-white/5"
                data-testid="creators-export-csv"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Download CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onExport("pdf")}
                className="text-zinc-200 focus:bg-white/5"
                data-testid="creators-export-pdf"
              >
                <FileText className="h-4 w-4 mr-2" /> Download PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="outline"
            size="sm"
            onClick={onImportClick}
            disabled={busy}
            className="h-9 border-white/10 bg-white/[0.02] text-zinc-200 hover:bg-white/5"
            data-testid="creators-import-btn"
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Import CSV
          </Button>

          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-0.5 flex">
            <button onClick={() => setView("table")} className={`h-8 px-3 rounded-md text-xs inline-flex items-center gap-1.5 ${view === "table" ? "bg-white/[0.06] text-zinc-100" : "text-zinc-400"}`} data-testid="view-table-btn">
              <List className="h-3.5 w-3.5" /> Table
            </button>
            <button onClick={() => setView("card")} className={`h-8 px-3 rounded-md text-xs inline-flex items-center gap-1.5 ${view === "card" ? "bg-white/[0.06] text-zinc-100" : "text-zinc-400"}`} data-testid="view-card-btn">
              <LayoutGrid className="h-3.5 w-3.5" /> Cards
            </button>
          </div>

          <FormDialog
            testid="creator-create"
            title="Add a new creator"
            description="Add a creator to your roster. You can edit details on the profile page later."
            submitLabel="Add Creator"
            trigger={
              <Button className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="add-creator-btn">
                + Add Creator
              </Button>
            }
            fields={[
              { key: "name", label: "Name", required: true, placeholder: "Riya Kapoor", gridCols: 2 },
              { key: "handle", label: "Handle", required: true, placeholder: "@riya.styles", gridCols: 2 },
              {
                key: "primaryPlatform",
                label: "Primary platform",
                type: "select",
                required: true,
                options: ["Instagram", "YouTube", "TikTok", "LinkedIn", "Twitter / X", "Snapchat"],
                gridCols: 2,
              },
              { key: "followers", label: "Followers", type: "number", placeholder: "250000", gridCols: 2 },
              { key: "location", label: "Location", placeholder: "Mumbai, MH", gridCols: 2 },
              { key: "niches", label: "Niches (comma-separated)", placeholder: "Fashion, Lifestyle", gridCols: 2 },
              { key: "er", label: "Engagement rate (%)", type: "number", placeholder: "6.2", gridCols: 2 },
              { key: "reelRate", label: "Reel rate (₹)", type: "number", placeholder: "75000", gridCols: 2 },
            ]}
            onSubmit={async (form) => {
              const body = {
                name: form.name,
                handle: form.handle,
                primaryPlatform: form.primaryPlatform,
                followers: Number(form.followers) || 0,
                er: Number(form.er) || 0,
                location: form.location || "",
                niches: (form.niches || "").split(",").map((s) => s.trim()).filter(Boolean),
                rating: 4,
                languages: ["English"],
                platforms: [{ name: form.primaryPlatform, followers: Number(form.followers) || 0 }],
                rateCard: { reel: Number(form.reelRate) || 0, post: 0, story: 0 },
                audience: { female: 50, male: 50, ageGroups: [] },
                avgViews: 0,
                history: [],
                avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(form.name)}`,
              };
              await createCreator(body);
              toast.success(`${body.name} added`);
              await load();
            }}
          />
        </div>
      </div>

      {/* Basic filter row */}
      <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Search name, handle, location…"
            className="pl-9 bg-white/[0.03] border-white/10 text-zinc-100"
            data-testid="creators-search-input"
          />
        </div>
        <Select value={filters.niche} onValueChange={(v) => setFilters((f) => ({ ...f, niche: v }))}>
          <SelectTrigger className="bg-white/[0.03] border-white/10 text-zinc-100" data-testid="creators-niche-select">
            <SelectValue placeholder="Niche" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10 max-h-72">
            <SelectItem value="All">All niches</SelectItem>
            {meta.niches.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.platform} onValueChange={(v) => setFilters((f) => ({ ...f, platform: v }))}>
          <SelectTrigger className="bg-white/[0.03] border-white/10 text-zinc-100" data-testid="creators-platform-select">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="All">All platforms</SelectItem>
            {meta.platforms.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select
          value={String(filters.minFollowers)}
          onValueChange={(v) => setFilters((f) => ({ ...f, minFollowers: Number(v) }))}
        >
          <SelectTrigger className="bg-white/[0.03] border-white/10 text-zinc-100" data-testid="creators-followers-select">
            <SelectValue placeholder="Followers" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            {FOLLOWER_TIERS.map((t) => (
              <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Advanced filter panel */}
      {showAdvanced && (
        <div
          className="rounded-xl border border-white/8 bg-white/[0.02] p-4 grid grid-cols-1 md:grid-cols-4 gap-3"
          data-testid="advanced-filters-panel"
        >
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">City</div>
            <Select value={filters.city} onValueChange={(v) => setFilters((f) => ({ ...f, city: v }))}>
              <SelectTrigger className="bg-white/[0.03] border-white/10 text-zinc-100" data-testid="creators-city-select">
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10 max-h-72">
                <SelectItem value="All">All cities</SelectItem>
                {meta.cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Language</div>
            <Select value={filters.language} onValueChange={(v) => setFilters((f) => ({ ...f, language: v }))}>
              <SelectTrigger className="bg-white/[0.03] border-white/10 text-zinc-100" data-testid="creators-language-select">
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10 max-h-72">
                <SelectItem value="All">All languages</SelectItem>
                {meta.languages.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Followers cap</div>
            <Select
              value={String(filters.maxFollowers)}
              onValueChange={(v) => setFilters((f) => ({ ...f, maxFollowers: Number(v) }))}
            >
              <SelectTrigger className="bg-white/[0.03] border-white/10 text-zinc-100" data-testid="creators-max-followers-select">
                <SelectValue placeholder="Max followers" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {MAX_FOLLOWER_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Price range (per reel)</div>
            <Select
              value={String(filters.priceIdx)}
              onValueChange={(v) => setFilters((f) => ({ ...f, priceIdx: Number(v) }))}
            >
              <SelectTrigger className="bg-white/[0.03] border-white/10 text-zinc-100" data-testid="creators-price-select">
                <SelectValue placeholder="Price" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {PRICE_RANGES.map((p, i) => (
                  <SelectItem key={p.label} value={String(i)}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Min rating</div>
            <Select
              value={String(filters.minRating)}
              onValueChange={(v) => setFilters((f) => ({ ...f, minRating: Number(v) }))}
            >
              <SelectTrigger className="bg-white/[0.03] border-white/10 text-zinc-100" data-testid="creators-min-rating-select">
                <SelectValue placeholder="Rating" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="0">Any rating</SelectItem>
                <SelectItem value="3">3.0+</SelectItem>
                <SelectItem value="4">4.0+</SelectItem>
                <SelectItem value="4.5">4.5+</SelectItem>
                <SelectItem value="4.8">4.8+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 flex items-end justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="text-zinc-400 hover:text-zinc-100"
              data-testid="creators-reset-filters"
            >
              Reset all filters
            </Button>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {activeFilterChips.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="creators-filter-chips">
          {activeFilterChips.map((chip) => (
            <button
              key={chip.key}
              onClick={() => clearChip(chip.key)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-blue-500/10 border border-blue-500/30 text-blue-200 hover:bg-blue-500/20"
            >
              {chip.label}
              <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      {/* Selection bar */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-2.5"
          data-testid="creators-selection-bar"
        >
          <div className="text-sm text-blue-100">
            {selectedIds.size} creator{selectedIds.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport("csv")}
              disabled={busy}
              className="h-8 border-white/10 bg-white/[0.04] text-zinc-100 hover:bg-white/10"
              data-testid="creators-selection-export-csv"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport("pdf")}
              disabled={busy}
              className="h-8 border-white/10 bg-white/[0.04] text-zinc-100 hover:bg-white/10"
              data-testid="creators-selection-export-pdf"
            >
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Export PDF
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="h-8 text-zinc-300 hover:text-zinc-100"
              data-testid="creators-selection-clear"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500" data-testid="creators-loading">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-white/8 bg-white/[0.02] p-12 text-center" data-testid="creators-empty-state">
          <p className="text-zinc-400">No creators match those filters.</p>
        </div>
      ) : view === "table" ? (
        <div className="rounded-xl border border-white/8 bg-white/[0.02] overflow-x-auto">
          <table className="w-full text-sm" data-testid="creators-table">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-500 bg-white/[0.02]">
              <tr className="text-left">
                <th className="py-3 px-4 w-10">
                  <Checkbox
                    checked={allSelectedOnPage}
                    onCheckedChange={toggleSelectAllOnPage}
                    aria-label="Select all on page"
                    data-testid="creators-select-all"
                  />
                </th>
                <th className="py-3 px-4 font-medium">Creator</th>
                <th className="py-3 px-4 font-medium">Platforms</th>
                <th className="py-3 px-4 font-medium">Followers</th>
                <th className="py-3 px-4 font-medium">ER</th>
                <th className="py-3 px-4 font-medium">Niche</th>
                <th className="py-3 px-4 font-medium">Location</th>
                <th className="py-3 px-4 font-medium">Rating</th>
                <th className="py-3 px-4 font-medium text-right">Reel rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-white/[0.02]" data-testid={`creator-row-${c.id}`}>
                  <td className="py-3 px-4">
                    <Checkbox
                      checked={selectedIds.has(c.id)}
                      onCheckedChange={() => toggleSelect(c.id)}
                      aria-label={`Select ${c.name}`}
                      data-testid={`creator-select-${c.id}`}
                    />
                  </td>
                  <td className="py-3 px-4">
                    <Link to={`/app/creators/${c.id}`} className="flex items-center gap-3 hover:text-blue-300">
                      <img src={c.avatar} alt="" className="h-9 w-9 rounded-full object-cover ring-1 ring-white/10" />
                      <div>
                        <div className="text-zinc-100 font-medium">{c.name}</div>
                        <div className="text-xs text-zinc-500">{c.handle}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2 text-zinc-300">
                      {(c.platforms || []).map((p) => (
                        <span key={p.name} title={p.name} className="h-6 w-6 rounded bg-white/[0.04] border border-white/8 flex items-center justify-center">
                          <PlatformIcon name={p.name} />
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-zinc-300">{formatFollowers(c.followers)}</td>
                  <td className="py-3 px-4 text-blue-300">{c.er}%</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1 flex-wrap">
                      {(c.niches || []).map((n) => (
                        <span key={n} className="text-[11px] px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/8 text-zinc-300">{n}</span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-zinc-400">{c.location}</td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1 text-amber-300">
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {c.rating}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-zinc-300">{formatINR(c.rateCard?.reel || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="creators-card-grid">
          {items.map((c) => (
            <div
              key={c.id}
              className={`group rounded-xl border bg-white/[0.02] p-5 transition-all ${
                selectedIds.has(c.id)
                  ? "border-blue-500/50 ring-1 ring-blue-500/30"
                  : "border-white/8 hover:border-blue-500/30 hover:-translate-y-1"
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={selectedIds.has(c.id)}
                  onCheckedChange={() => toggleSelect(c.id)}
                  aria-label={`Select ${c.name}`}
                  data-testid={`creator-card-select-${c.id}`}
                  className="mt-1"
                />
                <Link to={`/app/creators/${c.id}`} className="flex-1 flex items-center gap-3 hover:text-blue-300">
                  <img src={c.avatar} alt="" className="h-12 w-12 rounded-full object-cover ring-1 ring-white/10" />
                  <div>
                    <div className="font-medium text-zinc-100">{c.name}</div>
                    <div className="text-xs text-zinc-500">{c.handle}</div>
                  </div>
                </Link>
                <span className="text-xs inline-flex items-center gap-1 text-amber-300">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {c.rating}
                </span>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-zinc-300">{formatFollowers(c.followers)}</span>
                <span className="text-blue-300 text-xs px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/30">{c.er}% ER</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(c.niches || []).map((n) => (
                  <span key={n} className="text-[11px] px-2 py-0.5 rounded bg-white/[0.04] border border-white/8 text-zinc-300">{n}</span>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-xs text-zinc-500">
                <span>{c.location}</span>
                <span>{formatINR(c.rateCard?.reel || 0)}/reel</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.total > PAGE_SIZE && (
        <div
          className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3"
          data-testid="creators-pagination"
        >
          <div className="text-xs text-zinc-500">
            Page {data.page} of {data.total_pages || 1}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={data.page <= 1 || loading}
              onClick={goPrev}
              className="h-8 border-white/10 bg-white/[0.02] text-zinc-200 hover:bg-white/5"
              data-testid="creators-page-prev"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.page >= (data.total_pages || 1) || loading}
              onClick={goNext}
              className="h-8 border-white/10 bg-white/[0.02] text-zinc-200 hover:bg-white/5"
              data-testid="creators-page-next"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Creators;
